import _sortBy from 'lodash/sortBy';
import { getColor } from 'volta/utils/style-utils';

import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { EChartsOption } from 'volta/components/bv-chart';
import type IntlService from 'ember-intl/services/intl';
import { isPresent } from '@ember/utils';
import { makeNumberFormatter } from 'volta/utils/text-utils';

type TChart = 'avgStockValue' | 'stockouts' | 'planningBuffer' | 'executionBuffer';

type TSeriesKey =
  | 'avgStockValue'
  | 'stockoutsWithDemand'
  | 'stockouts'
  | 'bufferCritical'
  | 'bufferHigh'
  | 'bufferMedium'
  | 'bufferLow'
  | 'bufferOTOG'
  | 'eaBufferCritical'
  | 'eaBufferHigh'
  | 'eaBufferMedium'
  | 'eaBufferLow'
  | 'eaBufferOTOG';

type TSeriesInput = {
  inventoryDate: string[];
} & Record<TSeriesKey, number[] | undefined>;

type TSeriesData = Record<TSeriesKey, [string, number | '-'][]>;

type TSeriesOption = {
  key: TSeriesKey;
  label: string;
  color?: string;
  type: 'currency' | 'number' | 'percent';
};

const SeriesDefefitions: Record<TChart, Array<TSeriesOption>> = {
  avgStockValue: [
    {
      key: 'avgStockValue',
      label: 'dashboardPage.kpi.avgStockValue.title',
      color: getColor('secondary'),
      type: 'currency'
    }
  ],
  stockouts: [
    {
      key: 'stockouts',
      label: 'dashboardPage.kpi.stockouts.title',
      color: getColor('warning'),
      type: 'number'
    },
    {
      key: 'stockoutsWithDemand',
      label: 'dashboardPage.kpi.stockoutsWithDemand.title',
      color: getColor('severe'),
      type: 'number'
    }
  ],
  planningBuffer: [
    { key: 'bufferCritical', label: 'redBase', color: getColor('critical'), type: 'percent' },
    { key: 'bufferHigh', label: 'redSafety', color: getColor('danger'), type: 'percent' },
    { key: 'bufferMedium', label: 'yellow', color: getColor('warning'), type: 'percent' },
    { key: 'bufferLow', label: 'green', color: getColor('success'), type: 'percent' },
    { key: 'bufferOTOG', label: 'otog', color: getColor('primary'), type: 'percent' }
  ],
  executionBuffer: [
    { key: 'eaBufferCritical', label: 'redBase', color: getColor('critical'), type: 'percent' },
    { key: 'eaBufferHigh', label: 'redSafety', color: getColor('danger'), type: 'percent' },
    { key: 'eaBufferMedium', label: 'yellow', color: getColor('warning'), type: 'percent' },
    { key: 'eaBufferLow', label: 'green', color: getColor('success'), type: 'percent' },
    { key: 'eaBufferOTOG', label: 'otog', color: getColor('primary'), type: 'percent' }
  ]
};

interface IArgs {
  chart: TChart;
  series?: TSeriesInput;
  yAxisFormat?: 'shortScale' | 'percentage';
  loading: boolean;
}

export default class DashboardKpiChart extends Component<IArgs> {
  @service intl!: IntlService;

  @cached
  get data(): TSeriesData {
    const data = {} as TSeriesData;
    const { series, chart } = this.args;

    const defs = SeriesDefefitions[chart] ?? [];

    if (defs.length && series?.inventoryDate?.length) {
      const dates = series.inventoryDate;

      dates.forEach((date, index) => {
        defs.forEach((opt: TSeriesOption) => {
          const value = series[opt.key]?.[index];

          if (!data[opt.key]) {
            data[opt.key] = [];
          }

          data[opt.key].push([date, isPresent(value) ? Math.abs(value!) : '-']);
        });
      });
    }

    return data;
  }

  get chartOptions(): EChartsOption {
    const { data } = this;
    const { chart } = this.args;
    const defs = SeriesDefefitions[chart] ?? [];

    const valueFormatter = makeNumberFormatter(this.args.yAxisFormat, this.intl);
    const dates = [...new Set(this.args.series?.inventoryDate ?? [])].sort();
    return {
      legend: undefined,
      grid: {
        bottom: 20,
        top: 20
      },
      xAxis: { data: dates },
      yAxis: {
        axisLabel: {
          formatter: valueFormatter
        }
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: valueFormatter
      },
      dataZoom: [{ type: 'inside', disabled: true }],
      series: defs.map((opt: TSeriesOption) => {
        const d = _sortBy(data[opt.key], (datum) => datum[0]);
        return {
          name: this.intl.t(opt.label),
          data: d,
          type: 'bar',
          showSymbol: false,
          color: opt.color,
          stack: chart,
          barCategoryGap: '5%'
        };
      })
    };
  }
}
